import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GridRails } from './GridRails';
import { PhilosophyGeometry } from './PhilosophyGeometry';
import { SectionEyebrow } from './SectionEyebrow';
import { Reveal } from './Reveal';
import { FillLabel } from './FillLabel';
import { philosophyTenets } from '../data/philosophy';
const LINE = 'border-parchment-line';
// Frame = the vertical page rails; cells are padded so text sits on the page's content edge
const FRAME = 'mx-auto max-w-[1720px] px-5 md:px-16 lg:px-24';
// Two cells; the right cell's divider sits 32px left of centre so its text starts exactly at centre,
// matching the right column of the Ecosystem section above.
const ROW = 'grid md:grid-cols-2';
const LEFT = 'px-4 md:pl-8 md:pr-16';
const RIGHT = `px-4 md:-ml-8 md:border-l md:pl-8 md:pr-8 ${LINE}`;

/** Full-width line spanning screen edge to edge. */
function Rule() {
  return <div aria-hidden="true" className={`relative z-[6] w-full border-t ${LINE}`} data-id="element-337" data-mp="292llkm428kpl" />;
}

/** True below the md breakpoint — the drawing moves to the top of the section on phones. */
function useIsMobile() {
  const query = '(max-width: 767px)';
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}
export function PhilosophySection() {
  const mobile = useIsMobile();
  return <section id="philosophy" className="relative bg-parchment-bg pb-[48px] pt-0 text-parchment-ink md:pb-[72px] md:pt-[96px]" data-id="element-338" data-mp="39oygi7e702e1">
      <GridRails tone="blush" data-id="element-339" data-mp="3iod7d7y9uez2" />

      <div className="relative z-10" data-id="element-340" data-mp="3ip4t7r22gyc5">
        {/* Phones: the drawing opens the section, shorter, edge to edge between the rails */}
        {mobile && <>
            <div className={FRAME} data-id="element-341" data-mp="1nl6sod1il4aw">
              <div className="relative h-[260px] overflow-hidden" data-id="element-342" data-mp="1nm8d384f735a">
                <PhilosophyGeometry className="h-full w-full" data-id="element-343" data-mp="1imdo5a47gnmv" />
              </div>
            </div>
            <Rule data-id="element-344" data-mp="1xaqcbkx1x26t" />
          </>}

        {/* Label row */}
        <div className={FRAME} data-id="element-345" data-mp="1xd322k91wguf">
          <div className={ROW} data-id="element-346" data-mp="1sjrrc814bfx8">
            <div className={`${LEFT} hidden md:block`} data-id="element-347" data-mp="1sji1ckg2p8rj" />
            <div className={`${RIGHT} pb-5 pt-8 md:border-l-0 md:pt-0`} data-id="element-348" data-mp="26benm7v28og0">
              <Reveal data-id="element-349" data-mp="26drexbmpkb5q">
                <SectionEyebrow label="Philosophy" data-id="element-350" data-mp="22acyijuuxsrr" />
              </Reveal>
            </div>
          </div>
        </div>
        <Rule data-id="element-351" data-mp="2ga47u7afikzf" />

        {/* Statement | visual */}
        <div className={FRAME} data-id="element-352" data-mp="2g7rqnwchwenf">
          <div className={ROW} data-id="element-353" data-mp="2ba9hc4ukfxew">
            <Reveal className={`${LEFT} flex flex-col gap-6 py-8 md:gap-8 md:py-12`} data-id="element-354" data-mp="2bc2odl93cjnf">
              <h2 className="max-w-[600px] font-display text-[34px] leading-[1.02] tracking-[-0.025em] md:text-[52px] lg:text-[60px]" data-id="element-355" data-mp="lww9i5n8vr91">
                What we hold steady,{' '}
                <span className="font-editorial italic tracking-[-0.01em]" data-id="element-356" data-mp="2sjk3vutvzu43">holds everything else.</span>
              </h2>
              <p className="max-w-[420px] text-[14.5px] leading-[1.5] text-parchment-ink/60" data-id="element-357" data-mp="2xi56g0wm2pvp">
                Wealth, to us, is not the aim but the residue of a well-ordered life. Adrian Kays founded KAYS &amp; KAYS on
                one quiet conviction: choose a single constant — quality — and keep it without exception. What is held
                with care returns, and grows, in everything it touches.
              </p>
              <Link to="/manuscript" className="btn-fill btn-fill-dark inline-flex h-[48px] w-fit items-center whitespace-nowrap border border-parchment-ink/25 px-6 font-accent text-[11px] uppercase tracking-[0.08em] text-parchment-ink/85" data-id="element-358" data-mp="2eu0673f5xn1n">
                <FillLabel data-id="element-359" data-mp="3u9yzx9o19gid">Read the 0/1 Manuscript</FillLabel>
              </Link>
            </Reveal>
            {/* Desktop: visual fills the whole right cell, edge to edge between the lines */}
            {!mobile && <div className={`${RIGHT} relative min-h-[560px] !p-0`} data-id="element-360" data-mp="1z46kaxyw4oky">
                <div className="absolute inset-0 overflow-hidden" data-id="element-361" data-mp="1v57ap027s3n6">
                  <PhilosophyGeometry className="h-full w-full" data-id="element-362" data-mp="1q4tcsaubua2v" />
                </div>
              </div>}
          </div>
        </div>

        {/* Tenets — one full-width row each */}
        {philosophyTenets.map((t, i) => <React.Fragment key={t.title} data-id="element-363" data-mp="1g8pyy7q44n03" data-mpi={i}>
            <Rule data-id="element-364" data-mp="1bgyi4owrdvn5" />
            <div className={FRAME} data-id="element-365" data-mp="1bggayx8k5x0g">
              <Reveal delay={i * 0.07} className={ROW} data-id="element-366" data-mp="1bff311mu9qmy">
                <div className={`${LEFT} pt-5 md:py-6`} data-id="element-367" data-mp="17ebnkg8pdwm9">
                  {/* Same square-marker label as the "PHILOSOPHY" eyebrow */}
                  <h3 className="flex items-center gap-3 font-accent text-[11px] font-normal uppercase tracking-[0.08em] text-ink/80" data-id="element-368" data-mp="12huynjtoje84">
                    <span aria-hidden="true" className="inline-block h-[6px] w-[6px] shrink-0 bg-ink" data-id="element-369" data-mp="shuexwrwew3e" />
                    {t.title}
                  </h3>
                </div>
                <div className={`${RIGHT} pb-5 pt-2.5 md:py-6`} data-id="element-370" data-mp="ig034t23pcd2">
                  <p className="max-w-[520px] text-[14.5px] leading-[1.5] text-parchment-ink/65" data-id="element-371" data-mp="nbp6l7h8rden">
                    <span className="mb-2 block font-display text-[19px] leading-[1.2] tracking-[-0.015em] text-parchment-ink md:mb-2.5 md:text-[22px]" data-id="element-372" data-mp="sdekqvzrg76y">
                      {t.kicker}
                    </span>
                    {t.body}
                  </p>
                </div>
              </Reveal>
            </div>
          </React.Fragment>)}
        <Rule data-id="element-373" data-mp="9lviwshh2z6i" />
      </div>
    </section>;
}